import { useCanWrite } from '@artic/api-client';
import { Button } from '@artic/ui/button';
import { toast } from '@artic/ui/toast';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { apiErrorMessage } from '../_shared/api';
import { useUnarchiveProject } from './queries';

interface UnarchiveProjectButtonProps extends Pick<
  ComponentProps<typeof Button>,
  'variant' | 'size' | 'className'
> {
  project: { project_id: string; project_name: string };
}

/**
 * ENG-981: restores an archived project straight away — it is the safe
 * direction, so there is no confirmation. Shown on archived rows of the
 * Projects list and in an archived project's header.
 */
export function UnarchiveProjectButton({ project, ...buttonProps }: UnarchiveProjectButtonProps) {
  const { t } = useTranslation('project');
  const canWrite = useCanWrite('planner');
  const unarchive = useUnarchiveProject();

  // Permissions v1: a read-only user has no way back from archived either.
  if (!canWrite) return null;

  function handleUnarchive() {
    unarchive.mutate(project.project_id, {
      onSuccess: () => {
        toast.success(t('archive.unarchived', { name: project.project_name }));
      },
      onError: (error) => {
        toast.error(apiErrorMessage(error, t('archive.unarchiveFailed')));
      },
    });
  }

  return (
    <Button {...buttonProps} onClick={handleUnarchive} disabled={unarchive.isPending}>
      {t('archive.unarchive')}
    </Button>
  );
}
